import { Box } from '@dtx-company/design-system/src'
import { BuyPlanPageContent } from '../../../../components/pages/BuyPlanPage/BuyPlanPageContent'
import { FlowcodeLimitUpsellModalProps } from './FlowcodeLimitUpsellModalProps'
import { LockModalGraphic } from '@app/common/src/constants/lockModal'
import { LockModalVariants } from '@dtx-company/inter-app/src/constants/lockModal'
import { PlanPurchaseExperienceDialog } from '@app/common/src/components/PlanPurchaseExperienceDialog'
import { PlanPurchaseProvider } from '@app/plans/src/purchase/PlanPurchaseProvider/PlanPurchaseProvider'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { PrismicUpsellModalContent } from '../PrismicUpsellModalContent/PrismicUpsellModalContent'
import { PromoAsset } from '@app/common/src/components/LockModal/PromoAsset/PromoAsset'
import { UpsellModal } from '../UpsellModal/UpsellModal'
import { UpsellModalStep } from '../UpsellModal/UpsellModalStep'
import { deserializePlanTypeQueryParam } from '@dtx-company/true-common/src/query-string-serialization/planType/deserializePlanTypeQueryParam'
import { useBillingPlanType } from '@app/common/src/hooks/useBillingPlanType/useBillingPlanType'
import { useFlowExperiment } from '@dtx-company/inter-app/src/hooks/useFlowExperiment/useFlowExperiment'
import { useFlowcodeLimitModalDocId } from './useFlowcodeLimitModalDocId'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import React, { forwardRef, useEffect, useState } from 'react'

// these graphics are carried over from the giant lock modals data structure
// as an interim solution to migrating to a more flexible upsell experience.
// In the future these will likely be removed in favor of an entirely better AIDA
// experience before the user reaches the upgrade section.

const PRO_GRAPHIC: LockModalGraphic = {
  header: '',
  customHeadingText: `You have hit your Flowcode limit!`,
  src: '/graphics/lockModal/fc-pro-code-limit3-2x.png',
  height: 663,
  width: 305,
  bg: '#242424',
  hideCompanies: true
}

const PRO_PLUS_GRAPHIC: LockModalGraphic = {
  header: 'Reach, engage & convert customers with more Flowcodes',
  customHeadingText: 'You have hit your Flowcode limit!',
  src: '/graphics/lockModal/fc-pro-code-limit.svg',
  bg: '/graphics/lockModal/pro-graphic-background.svg'
}

const PRO_FLEX_GRAPHIC: LockModalGraphic = {
  header: 'Reach, engage & convert customers with more Flowcodes',
  src: '/graphics/lockModal/fc-pro-code-limit.svg',
  bg: '/graphics/lockModal/pro-graphic-background.svg'
}

/**
 * Use this upsell experience whenever a user hits their Flowcode limit.
 *
 * It will determine how to upsell them based on their current plan limit and what plans
 * are available to provide them with a higher limit.
 *
 * Generally this modal should be opened when the user attempts an action, such as creating
 * a single Flowcode or batch of Flowcodes, which would put them over their limit.
 *
 * The modal may be dismissed, returning the user to the screen they were previously at.
 * If the user is successfully upsold, the action they initially took should be automatically
 * re-executed in order to provide a seamless experience.
 *
 * @see UpsellModal
 * @see LimitUpsellModal
 *
 * @example
 * function createFlowcode() {
 *   try {
 *     // do things
 *   } catch (error) {
 *     if (error instanceof FlowcodeLimitError) {
 *       setLimitModalOpen(true)
 *     } else {
 *       // handle things
 *     }
 *   }
 * }
 * <FlowcodeLimitUpsellModal
 *   open={limitModalOpen}
 *   onClose={(reason) => {
 *     setLimitModalOpen(false)
 *     if (reason === 'success') {
 *       createFlowcode()
 *     }
 *   }}
 * />
 */
export const FlowcodeLimitUpsellModal = forwardRef<HTMLDivElement, FlowcodeLimitUpsellModalProps>(
  function FlowcodeLimitUpsellModal(props, ref): JSX.Element | null {
    const [nextPlanTypeForBasicSetting] = useFlowExperiment('upsell_basic_flowcode_limit')
    const nextPlanTypeForBasic = nextPlanTypeForBasicSetting
      ? deserializePlanTypeQueryParam(nextPlanTypeForBasicSetting)
      : undefined
    const { isMobile } = useScreenSizeBreakpoints()
    // TODO: build a hook that determines the next plan type based on /available-plans
    const billingPlanType = useBillingPlanType()
    const nextPlanType = (() => {
      switch (billingPlanType) {
        case PlanType.PRO_PLUS_ORG:
          return PlanType.PRO_FLEX
        case PlanType.PRO:
          return PlanType.PRO_PLUS_ORG
      }
      if (nextPlanTypeForBasic) {
        return nextPlanTypeForBasic
      }
      return PlanType.PRO
    })()

    const heapLockSelected = (() => {
      // TODO: add growth variant
      switch (nextPlanType) {
        case PlanType.PRO_FLEX:
          return 'fcProFlexCodeLimit'
        case PlanType.PRO_PLUS_ORG:
          return LockModalVariants.FC_PRO_PLUS_CODE_LIMIT.toString()
      }
      return LockModalVariants.FC_PRO_CODE_LIMIT.toString()
    })()

    const graphic: LockModalGraphic = (() => {
      switch (nextPlanType) {
        case PlanType.PRO_FLEX:
          return PRO_FLEX_GRAPHIC
        case PlanType.PRO_PLUS_ORG:
          return PRO_PLUS_GRAPHIC
      }
      return PRO_GRAPHIC
    })()

    const prismicUid = useFlowcodeLimitModalDocId(nextPlanType)

    const [everOpened, setEverOpened] = useState(false)
    useEffect(() => {
      if (props.open) {
        setEverOpened(true)
      }
    }, [props.open])

    if (!everOpened) return null

    const handleClose = (reason: 'success' | 'dismiss'): void => {
      props.onClose(reason)
    }

    return prismicUid ? (
      <PlanPurchaseProvider planType={nextPlanType}>
        <UpsellModal open={props.open} onClose={handleClose} heapId="ProFCLimitUpsellModal">
          <UpsellModalStep step={1}>
            <PrismicUpsellModalContent uid={prismicUid} />
          </UpsellModalStep>
          <UpsellModalStep step={2}>
            <Box
              sx={{
                height: '100%',
                width: '100%',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={theme => ({
                  height: isMobile ? `calc(${theme.spacing(14)} - 1.125rem)` : theme.spacing(14),
                  flexShrink: 0
                })}
              />
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <BuyPlanPageContent
                  lockSelected={heapLockSelected}
                  lockPrismicId={prismicUid}
                  onSuccessfulCheckout={() => {
                    props.onClose('success')
                  }}
                />
              </Box>
            </Box>
          </UpsellModalStep>
        </UpsellModal>
      </PlanPurchaseProvider>
    ) : (
      <PlanPurchaseExperienceDialog
        {...props}
        ref={ref}
        planType={nextPlanType}
        customHeadingText={graphic.customHeadingText}
        promoAsset={<PromoAsset graphic={graphic} />}
        promoBackgroundImageUrl={graphic.bg}
        promoHeading={graphic.header}
        promoHideCompanies={graphic.hideCompanies}
        heapLockSelected={heapLockSelected}
        onClose={() => props.onClose('dismiss')}
        onSuccessfulCheckout={() => props.onClose('success')}
      />
    )
  }
)
